import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import usePrevious from "../utils/usePrevious";

interface Props {
  totalLength: number;
  length?: number;
  buffer?: number;
  minItemHeight: number;
  renderItem: (index: number) => JSX.Element;
  className?: string;
}

// From https://github.com/aditya-kumawat/react-dynamic-virtual-scroll#readme

const VirtualScroll: React.FC<Props> = ({
  totalLength,
  length = 14,
  buffer = 1,
  minItemHeight,
  renderItem,
  className
}) => {
  const [offset, setOffset] = useState(0);
  const [lastScrollTop, setLastScrollTop] = useState(0);
  const [avgRowHeight, setAvgRowHeight] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const isInView = useCallback((container, element) => {
    const containerTop = container.offsetTop;
    const elementRect = element.getBoundingClientRect();
    const elementTop = elementRect.top;
    const elementHeight = elementRect.height;

    return elementHeight - (containerTop - elementTop) > 0;
  }, []);

  const updateOffset = useCallback(
    (prevOffset: number) => {
      const containerRefCurrent = containerRef.current;

      if (!containerRefCurrent) return;

      const offsetDiff = prevOffset - offset;

      let items = containerRefCurrent.querySelectorAll(".VS-item");
      let heightAdded = 0;
      let currOffset = prevOffset;
      let start = Math.min(offset, buffer);
      let end = start + offsetDiff;

      for (let i = Math.min(items.length, end) - 1; i >= start; i--) {
        let inView = isInView(containerRefCurrent, items[i]);

        if (inView) {
          currOffset--;
          let rowHeight = items[i].clientHeight;
          heightAdded += rowHeight;
        } else {
          break;
        }
      }

      if (items.length < end) {
        let diff = end - items.length;
        heightAdded += diff * minItemHeight;
        currOffset -= diff;
      }

      let newAvgRowHeight =
        currOffset === 0
          ? minItemHeight
          : (avgRowHeight * prevOffset - heightAdded) / currOffset;

      setOffset(currOffset);
      setAvgRowHeight(Math.max(minItemHeight, newAvgRowHeight));
    },
    [avgRowHeight, buffer, isInView, minItemHeight, offset]
  );

  const prevOffset = usePrevious(offset);
  useEffect(() => {
    if (prevOffset > offset) {
      updateOffset(prevOffset);
    }
  }, [offset, prevOffset, updateOffset]);

  const onScrollHandler = useCallback(() => {
    const containerRefCurrent = containerRef.current;

    if (!containerRefCurrent) return;

    const scrollTop = containerRefCurrent.scrollTop;
    const direction = Math.floor(scrollTop - lastScrollTop);

    if (direction === 0) return;

    const items = containerRefCurrent.querySelectorAll(".VS-item");
    let newOffset = offset;
    let newAvgRowHeight = avgRowHeight;
    const start = Math.min(offset, buffer);

    if (direction > 0) {
      if (offset < totalLength - length) {
        let heightAdded = 0;

        for (let i = start; i < items.length; i++) {
          let inView = isInView(containerRefCurrent, items[i]);
          let rowHeight = items[i].clientHeight;

          if (!inView) {
            heightAdded += rowHeight;
            newOffset++;
          } else {
            break;
          }
        }

        if (heightAdded < direction) {
          let heightLeft = direction - heightAdded;
          let offsetToBeAdded = Math.floor(heightLeft / minItemHeight);
          newOffset += offsetToBeAdded;
          heightAdded += offsetToBeAdded * minItemHeight;
        }

        newAvgRowHeight =
          newOffset > 0
            ? (offset * avgRowHeight + heightAdded) / newOffset
            : minItemHeight;

        setOffset(Math.min(newOffset, totalLength - length));
        setAvgRowHeight(Math.max(minItemHeight, newAvgRowHeight));
      }
    } else {
      let scrollDiff =
        items[start].getBoundingClientRect().y -
        containerRefCurrent.getBoundingClientRect().y;

      if (scrollDiff > 0) {
        let offsetDiff = Math.floor(scrollDiff / minItemHeight) || 1;

        let _newOffset = offset - offsetDiff;

        if (_newOffset < totalLength - (length + buffer)) {
          setOffset(Math.max(0, _newOffset));
        }
      }
    }

    setLastScrollTop(scrollTop);
  }, [
    avgRowHeight,
    buffer,
    isInView,
    lastScrollTop,
    length,
    minItemHeight,
    offset,
    totalLength
  ]);

  const top = useMemo(() => Math.max(0, offset - buffer), [buffer, offset]);
  const bottom = useMemo(
    () => Math.min(offset + (length + buffer) - 1, totalLength - 1),
    [buffer, length, offset, totalLength]
  );
  const topPadding = useMemo(
    () => Math.max(0, top * avgRowHeight),
    [avgRowHeight, top]
  );
  const bottomPadding = useMemo(
    () => Math.max(0, (totalLength - bottom - 1) * avgRowHeight),
    [avgRowHeight, bottom, totalLength]
  );

  const renderItems = useMemo(() => {
    return Array.from(
      {
        length: bottom - top + 1
      },
      function (_, index) {
        let rowIndex = top + index;
        let component = renderItem(rowIndex);

        return React.cloneElement(component, {
          key: rowIndex,
          className: ["VS-item", component.props.className].join(" ").trim()
        });
      }
    );
  }, [bottom, renderItem, top]);

  return (
    <div ref={containerRef} className={className} onScroll={onScrollHandler}>
      <div className="flex-shrink-0" style={{ height: topPadding }} />
      <div>{renderItems}</div>
      <div className="flex-shrink-0" style={{ height: bottomPadding }} />
    </div>
  );
};

export default VirtualScroll;
